import React from 'react';
import { Coins, Loader2, X } from 'lucide-react';

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  /** Credits shown for the primary action */
  credits: number;
  balance: number;
  confirmLabel?: string;
  /** Optional secondary action (e.g. generate one scene) */
  secondaryLabel?: string;
  secondaryCredits?: number;
  busy?: boolean;
  /** Blocks the primary action (e.g. a required choice in `children` is still empty). */
  confirmDisabled?: boolean;
  /** Primary action that costs nothing (e.g. a local crop) stays enabled at 0 credits. */
  allowFree?: boolean;
  error?: string | null;
  children?: React.ReactNode;
  onConfirm: () => void;
  onSecondary?: () => void;
  onCancel: () => void;
};

export default function CreditConfirmModal({
  open,
  title,
  subtitle,
  credits,
  balance,
  confirmLabel = 'Confirmar e gerar',
  secondaryLabel,
  secondaryCredits,
  busy,
  confirmDisabled,
  allowFree,
  error,
  children,
  onConfirm,
  onSecondary,
  onCancel,
}: Props) {
  if (!open) return null;
  const insufficientPrimary = balance < credits;
  const secondaryCost = secondaryCredits ?? 0;
  const insufficientSecondary = secondaryLabel ? balance < secondaryCost : false;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="frag-credit-title"
      className="frag-credit-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="frag-credit-card">
        <button
          type="button"
          aria-label="Fechar"
          disabled={busy}
          onClick={onCancel}
          className="frag-modal-close"
        >
          <X size={18} />
        </button>

        <h3 id="frag-credit-title" className="frag-modal-title">
          {title}
        </h3>
        {subtitle && <p className="frag-modal-sub">{subtitle}</p>}

        {children && <div style={{ marginBottom: '0.85rem' }}>{children}</div>}

        <div className="frag-credit-summary">
          <Coins size={20} color="#d4d4d8" />
          <div style={{ flex: 1 }}>
            <div className="frag-credit-summary-label">Créditos a debitar</div>
            <div className="frag-credit-summary-value">{credits}</div>
            {secondaryLabel && secondaryCredits != null && secondaryCredits !== credits && (
              <div className="frag-credit-summary-alt">
                Só uma cena: {secondaryCredits} cr
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="frag-credit-summary-label">Saldo</div>
            <div
              className="frag-credit-summary-balance"
              style={{ color: insufficientPrimary ? '#f87171' : '#86efac' }}
            >
              {balance}
            </div>
          </div>
        </div>

        {insufficientPrimary && (
          <p className="frag-error" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            Saldo insuficiente na organização.
          </p>
        )}
        {error && (
          <p className="frag-error" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            {error}
          </p>
        )}

        <div className="frag-credit-actions">
          <button type="button" className="btn-secondary" disabled={busy} onClick={onCancel}>
            Cancelar
          </button>
          {secondaryLabel && onSecondary && (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy || insufficientSecondary || secondaryCost < 1}
              onClick={onSecondary}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {secondaryLabel}
            </button>
          )}
          <button
            type="button"
            className="btn-primary"
            disabled={busy || confirmDisabled || insufficientPrimary || (!allowFree && credits < 1)}
            onClick={onConfirm}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {busy ? <Loader2 size={16} className="spin" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
